import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "@strapi/design-system"
import { cn } from "@/lib/utils"

function pagination(currentPage: number, pageCount: number, deltaValue: number) {
  const delta = deltaValue
  const range: number[] = []
  const rangeWithDots: Array<string | number> = []
  let l

  range.push(1)

  if (pageCount <= 1) return range

  for (let i = currentPage - delta; i <= currentPage + delta; i++)
    if (i < pageCount && i > 1) range.push(i)

  range.push(pageCount)

  for (const i of range) {
    if (l)
      if (i - l === 2) rangeWithDots.push(l + 1)
      else if (i - l !== 1) rangeWithDots.push("...")

    rangeWithDots.push(i)
    l = i
  }

  return rangeWithDots
}

interface PaginatorProps {
  page: number
  pageCount: number
}

const Paginator = ({ page, pageCount }: PaginatorProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Don't render pagination if there's only 1 page
  if (pageCount <= 1) return null

  const items = pagination(page, pageCount, 1)

  const handlePageChange = (newPage: number) => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set("page", newPage.toString())
    navigate(`${location.pathname}?${searchParams.toString()}`)
  }

  return (
    <div className="mt-4 flex items-center justify-center">
      <div className="flex items-center justify-center rounded-lg bg-gray-800 px-2 py-1.5">
        <div className="flex items-center gap-2">
          <Button
            variant="tertiary"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="flex size-8 items-center justify-center rounded-lg bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{"<"}</span>
          </Button>

          {items.map((item, idx) => {
            if (item === "...")
              return (
                <span key={`dots-${idx}`} className="px-2 text-gray-400">
                  ...
                </span>
              )

            return (
              <Button
                key={item}
                onClick={() => handlePageChange(item as number)}
                className={cn(
                  "flex size-8 items-center justify-center rounded-lg transition-colors",
                  item === page
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-700 text-gray-200 hover:bg-gray-600"
                )}
                variant="tertiary"
              >
                {item}
              </Button>
            )
          })}

          <Button
            variant="tertiary"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === pageCount}
            className="flex size-8 items-center justify-center rounded-lg bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{">"}</span>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Paginator
